import { useMutation, useQuery } from "@tanstack/react-query";
import { Check, Inbox } from "lucide-react";
import { toast } from "sonner";
import { ApiError, apiGet, apiPost, queryClient } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { QueryError } from "@/components/shared/query-error";
import { cn } from "@/lib/utils";

// More requests than this and the list scrolls inside its card, so a practice
// that told all its clients to invite it does not get a page-long card.
const SCROLL_AFTER = 5;

// A client who invited this practice from the Taxr app. Nothing is linked until
// the practice accepts here, or on the page the invitation email opens: both
// run the same accept logic on the server.
interface ClientRequest {
  id: string;
  client_email: string;
  requested_at: string;
  expires_at: string;
}

interface ClientRequestList {
  results: ClientRequest[];
}

interface AcceptedRequest {
  /** The client's id in the portal (/accountant/clients/:id). */
  id: string;
  invite_email: string;
  display_name: string;
}

const CLIENT_REQUESTS_KEY = ["client-invites"] as const;

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

/**
 * "Pending requests": the invitations clients have sent this practice, each
 * with an Accept button. Renders nothing while there are none, so a practice
 * with nothing waiting sees no card.
 */
export function PendingRequests() {
  const { data, isError, error } = useQuery({
    queryKey: CLIENT_REQUESTS_KEY,
    queryFn: () => apiGet<ClientRequestList>("/api/accountant/client-invites"),
    // A request can arrive any time, and the dashboard is where the practice
    // looks for it: always check again when the page opens.
    staleTime: 0,
  });

  const acceptMutation = useMutation({
    mutationFn: (request: ClientRequest) =>
      apiPost<AcceptedRequest>(
        `/api/accountant/client-invites/${request.id}/accept`,
      ),
    onSuccess: (accepted) => {
      toast.success(
        `${accepted.display_name || accepted.invite_email} added to your clients`,
      );
      // The client now belongs in the list and the counts above it.
      queryClient.invalidateQueries({ queryKey: CLIENT_REQUESTS_KEY });
      queryClient.invalidateQueries({ queryKey: ["accountant-users"] });
      queryClient.invalidateQueries({ queryKey: ["accountant-me"] });
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 404) {
        // Lapsed, or accepted from the email in the meantime.
        toast.error("This request is no longer available.");
        queryClient.invalidateQueries({ queryKey: CLIENT_REQUESTS_KEY });
        return;
      }
      toast.error(
        err instanceof Error ? err.message : "Could not accept this request",
      );
    },
  });

  if (isError) {
    return (
      <QueryError
        className="p-4"
        error={error}
        message="Could not load pending requests"
        onRetry={() =>
          queryClient.invalidateQueries({ queryKey: CLIENT_REQUESTS_KEY })
        }
      />
    );
  }

  const requests = data?.results ?? [];
  if (requests.length === 0) return null;

  return (
    <section aria-labelledby="pending-requests-title" data-testid="pending-requests">
      <Card>
        <CardContent className="space-y-3 p-4">
          <div className="flex items-start gap-3">
            <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-highlight-muted text-highlight-foreground">
              <Inbox className="size-5" aria-hidden />
            </div>
            <div>
              <h2 id="pending-requests-title" className="text-base font-semibold">
                Pending requests{" "}
                <span className="font-normal tabular-nums text-muted-foreground">
                  ({requests.length})
                </span>
              </h2>
              <p className="text-sm text-muted-foreground">
                These clients invited you from the Taxr app. Accept to add them
                to your clients and see their receipts.
              </p>
            </div>
          </div>

          <ul
            className={cn(
              "divide-y rounded-lg border",
              requests.length > SCROLL_AFTER && "max-h-80 overflow-y-auto",
            )}
          >
            {requests.map((request) => {
              const accepting =
                acceptMutation.isPending &&
                acceptMutation.variables?.id === request.id;
              return (
                <li
                  key={request.id}
                  data-testid={`pending-request-${request.client_email}`}
                  className="flex flex-col gap-2 p-3 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="min-w-0">
                    <p className="truncate font-medium">
                      {request.client_email}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Requested {formatDate(request.requested_at)}. Expires{" "}
                      {formatDate(request.expires_at)}.
                    </p>
                  </div>
                  <Button
                    size="sm"
                    className="cursor-pointer"
                    aria-label={`Accept ${request.client_email}`}
                    data-testid={`accept-request-${request.client_email}`}
                    disabled={acceptMutation.isPending}
                    onClick={() => acceptMutation.mutate(request)}
                  >
                    <Check className="mr-1.5 size-4" aria-hidden />
                    {accepting ? "Accepting..." : "Accept"}
                  </Button>
                </li>
              );
            })}
          </ul>
        </CardContent>
      </Card>
    </section>
  );
}
